import React, { JSX, useCallback } from 'react';
import { generatePath, Link } from 'react-router-dom';
import { CellProps } from 'react-table';
import { Button, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { BooleanValueIcon, ErrorMessages, MainPageContent, PageHeading, Table } from 'common/components';
import { useExecuteWithConfirmation } from 'common/utils';
import { config } from 'config/config';
import { Routes } from 'config/routes';
import { useTranslations } from 'contexts/LocalizationContext';
import { useAnonymizeApplicationUser, useApplicationUsersList } from 'services/application-users';

import { useDebounce } from '../../common/hooks/useDebounce';
import { DEBOUNCE_SEARCH_TIME } from '../../config/configConsts';
import { TokensModal } from './components/TokensModal';
import { ApplicationUser } from './models/application-user';
import { UserType } from './models/enums/user-type.enum';
import AnonymizedUserFallbackValue from './AnonymizedUserFallbackValue';

const ApplicationUsersPage = (): JSX.Element => {
  const translations = useTranslations();
  const [searchText, setSearchText] = React.useState('');
  const debouncedSearch = useDebounce(searchText, DEBOUNCE_SEARCH_TIME);
  const [page, setPage] = React.useState(1);
  const [selectedUser, setSelectedUser] = React.useState<ApplicationUser | null>(null);
  const [isTokensModalOpen, setIsTokensModalOpen] = React.useState(false);

  const {
    data,
    isFetching,
    error: applicationUsersError,
  } = useApplicationUsersList({
    searchText: debouncedSearch,
    limit: config.paginationSize,
    page,
  });

  const { mutate: sendAnonymizeUserRequest, isLoading: isAnonymizingInProgress } = useAnonymizeApplicationUser();

  const { executeWithConfirmation: triggerUserAnonymization } = useExecuteWithConfirmation(
    translations('users_anonymize_confirmation'),
    sendAnonymizeUserRequest,
  );

  const handleOpenTokensModal = useCallback((user: ApplicationUser) => {
    setSelectedUser(user);
    setIsTokensModalOpen(true);
  }, []);

  const handleCloseTokensModal = () => {
    setSelectedUser(null);
    setIsTokensModalOpen(false);
  };

  const handleAnonymizeUser = useCallback(
    (original: ApplicationUser) => {
      return () => triggerUserAnonymization(original.id);
    },
    [triggerUserAnonymization],
  );

  const columns = React.useMemo(
    () => [
      {
        id: '1',
        Header: translations('users_table_corporate_unique_id'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<ApplicationUser>) => <AnonymizedUserFallbackValue value={original.corporateUniqueId} />,
      },
      {
        id: '2',
        Header: translations('users_table_user_id'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<ApplicationUser>) => {
          if (!original.userTokens || original.userTokens.length === 0) {
            return <span>{original.id}</span>;
          }

          return (
            <Button size="sm" variant="outline" onClick={() => handleOpenTokensModal(original)}>
              {translations('users_view_tokens_button')}
            </Button>
          );
        },
      },
      {
        id: '3',
        Header: translations('users_table_first_name'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<ApplicationUser>) => <AnonymizedUserFallbackValue value={original.firstName} />,
      },
      {
        id: '4',
        Header: translations('users_table_last_name'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<ApplicationUser>) => <AnonymizedUserFallbackValue value={original.lastName} />,
      },
      {
        id: '5',
        Header: translations('users_table_email'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<ApplicationUser>) => <AnonymizedUserFallbackValue value={original.email} />,
      },
      {
        id: '6',
        Header: translations('users_table_language'),
        accessor: 'language' as const,
      },
      {
        id: '7',
        Header: translations('users_table_skip_payments'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<ApplicationUser>) => <BooleanValueIcon value={original.skipPayments} />,
      },
      {
        id: 'actions',
        Header: translations('users_table_actions'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<ApplicationUser>) => (
          <Menu isLazy>
            <MenuButton as={Button} size="sm" variant="outline">
              {translations('menu_open_menu')}
            </MenuButton>
            <Portal>
              <MenuList zIndex={10} fontSize={'14px'}>
                <MenuItem as={Link} to={generatePath(Routes.USERS_ASSIGNED_ITEMS_PATH, { userId: original.id })}>
                  {translations('users_items_button')}
                </MenuItem>
                {original.userType === UserType.app ? (
                  <MenuItem as={Link} to={generatePath(Routes.USERS_ECO_FEE_PATH, { userId: original.id })}>
                    {translations('users_eco_fee_button')}
                  </MenuItem>
                ) : null}
                {original.userType === UserType.app ? (
                  <MenuItem as={Link} to={generatePath(Routes.USERS_PROFILE_SETTINGS_PATH, { userId: original.id })}>
                    {translations('profile_settings_button')}
                  </MenuItem>
                ) : null}
                <MenuItem color="red.600" onClick={handleAnonymizeUser(original)}>
                  {translations('users_anonymize_button')}
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        ),
      },
    ],
    [translations, handleOpenTokensModal, handleAnonymizeUser],
  );

  return (
    <MainPageContent>
      <PageHeading>{translations('users_header')}</PageHeading>
      <ErrorMessages errors={applicationUsersError} />
      <Table<ApplicationUser>
        isLoading={isFetching || isAnonymizingInProgress}
        data={data?.items}
        columns={columns}
        page={page}
        onPageChanged={setPage}
        totalPages={data?.totalPages}
        searchText={searchText}
        onSearchTextChanged={setSearchText}
        searchboxPlaceholder={translations('users_searchbox')}
        searchEnabled
        paginationEnabled
      />
      {selectedUser && (
        <TokensModal
          isModalVisible={isTokensModalOpen}
          onModalClose={handleCloseTokensModal}
          tokens={selectedUser.userTokens}
          userId={selectedUser.id}
        />
      )}
    </MainPageContent>
  );
};

export default ApplicationUsersPage;
